/**
 * Treppan Academy - Exam Result
 * Shows score, grade, and certificate download option
 */

import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  Trophy, Award, XCircle, RotateCcw, Download, 
  ChevronRight, CheckCircle, Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  gold: '#D4AF37',
  success: '#10B981',
  fail: '#EF4444',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

export default function ExamResult() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const result = location.state?.result;
  
  if (!result) {
    // Redirect back to course if no result data
    navigate(`/academy/courses/${courseId}`);
    return null;
  }

  const { attempt, certificate } = result;
  const { score, passed, grade, correct_count, total_questions } = attempt;
  
  const isDistinction = grade === 'distinction';
  const accentColor = passed ? (isDistinction ? colors.gold : colors.success) : colors.fail;

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: colors.background }}>
      <div 
        className="max-w-lg w-full rounded-xl overflow-hidden"
        style={{ 
          backgroundColor: colors.surface, 
          border: `1px solid ${colors.border}`,
          boxShadow: passed ? `0 0 60px ${accentColor}20` : 'none'
        }}
      >
        {/* Header Graphic */}
        <div 
          className="relative h-48 flex items-center justify-center"
          style={{ 
            background: passed 
              ? `linear-gradient(135deg, ${accentColor}20 0%, transparent 50%)`
              : `linear-gradient(135deg, ${colors.fail}10 0%, transparent 50%)`
          }}
        >
          {/* Decorative circles */}
          <div 
            className="absolute w-64 h-64 rounded-full opacity-20"
            style={{ 
              background: `radial-gradient(circle, ${accentColor} 0%, transparent 70%)`,
              filter: 'blur(40px)'
            }}
          />
          
          {/* Main Icon */}
          <div 
            className="relative w-28 h-28 rounded-full flex items-center justify-center"
            style={{ 
              backgroundColor: `${accentColor}20`,
              border: `4px solid ${accentColor}`
            }}
          >
            {passed ? (
              isDistinction ? (
                <Trophy className="h-14 w-14" style={{ color: accentColor }} />
              ) : (
                <Award className="h-14 w-14" style={{ color: accentColor }} />
              )
            ) : (
              <XCircle className="h-14 w-14" style={{ color: accentColor }} />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Status Badge */}
          <Badge 
            className="mb-4 uppercase text-xs tracking-widest px-4 py-1"
            style={{ 
              backgroundColor: `${accentColor}20`,
              color: accentColor,
              border: `1px solid ${accentColor}30`
            }}
          >
            {passed ? (isDistinction ? 'Distinction' : 'Passed') : 'Not Passed'}
          </Badge>
          
          {/* Title */}
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            {passed ? 'Congratulations!' : 'Keep Learning!'}
          </h1>
          
          <p className="mb-8" style={{ color: colors.textBody }}>
            {passed 
              ? "You've successfully passed the certification exam!"
              : "Don't worry, you can retake the exam after reviewing the course."}
          </p>

          {/* Score Display */}
          <div 
            className="relative w-40 h-40 mx-auto mb-8"
            style={{ 
              background: `conic-gradient(${accentColor} ${score * 3.6}deg, ${colors.background} 0deg)`,
              borderRadius: '50%',
              padding: '8px'
            }}
          >
            <div 
              className="w-full h-full rounded-full flex flex-col items-center justify-center"
              style={{ backgroundColor: colors.surface }}
            >
              <span 
                className="text-5xl font-bold"
                style={{ color: accentColor, fontFamily: 'Outfit, sans-serif' }}
              >
                {score}%
              </span>
              <span className="text-sm" style={{ color: colors.textMuted }}>
                Your Score
              </span>
            </div>
          </div>

          {/* Stats */}
          <div 
            className="grid grid-cols-3 gap-4 p-4 rounded-xl mb-8"
            style={{ backgroundColor: colors.background }}
          >
            <div className="text-center">
              <p 
                className="text-2xl font-bold"
                style={{ color: colors.success, fontFamily: 'Outfit, sans-serif' }}
              >
                {correct_count}
              </p>
              <p className="text-xs" style={{ color: colors.textMuted }}>Correct</p>
            </div>
            <div className="text-center">
              <p 
                className="text-2xl font-bold"
                style={{ color: colors.fail, fontFamily: 'Outfit, sans-serif' }}
              >
                {total_questions - correct_count}
              </p>
              <p className="text-xs" style={{ color: colors.textMuted }}>Wrong</p>
            </div>
            <div className="text-center">
              <p 
                className="text-2xl font-bold"
                style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
              >
                {total_questions}
              </p>
              <p className="text-xs" style={{ color: colors.textMuted }}>Total</p>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {passed && certificate && (
              <Button 
                className="w-full rounded-full font-semibold"
                style={{ backgroundColor: accentColor, color: '#000' }}
                onClick={() => navigate(`/academy/certificates/${certificate.id}`)}
                data-testid="view-certificate-btn"
              >
                <Award className="h-4 w-4 mr-2" />
                View Certificate
              </Button>
            )}
            
            {!passed && (
              <Button 
                className="w-full rounded-full font-semibold"
                style={{ backgroundColor: colors.gold, color: '#000' }}
                onClick={() => navigate(`/academy/courses/${courseId}`)}
                data-testid="review-course-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Review Course & Retry
              </Button>
            )}
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => navigate('/academy')}
              style={{ borderColor: colors.border, color: colors.textBody }}
              data-testid="back-to-academy-btn"
            >
              Back to Academy
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
